/**
 * Polls /version.json every 2 minutes and on window focus.
 * If the build timestamp has changed since the tab was opened,
 * the page reloads automatically — no hard-refresh needed from the user.
 */
import { useEffect, useRef } from 'react'

const POLL_MS = 2 * 60 * 1000  // 2 minutes

async function fetchVersion(): Promise<number | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const json = await res.json()
    return typeof json.v === 'number' ? json.v : null
  } catch {
    return null
  }
}

export function useVersionCheck() {
  const baseline = useRef<number | null>(null)

  useEffect(() => {
    // Only active in production — dev server has no version.json
    if (!import.meta.env.PROD) return

    const check = async () => {
      const v = await fetchVersion()
      if (v === null) return
      if (baseline.current === null) {
        baseline.current = v          // store version from first load
      } else if (v !== baseline.current) {
        window.location.reload()      // new deploy detected — reload silently
      }
    }

    check()
    const timer = setInterval(check, POLL_MS)
    window.addEventListener('focus', check)

    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', check)
    }
  }, [])
}
